/**
 * Turning a report into a PDF.
 *
 * The page is rasterised through the browser and placed into A4 pages. That is
 * what makes Arabic work at all — the browser has shaped and ordered the text
 * long before capture, and jsPDF's own fonts can do neither — and it guarantees
 * the file matches the preview, because it *is* the preview.
 *
 * jsPDF's own `.html()` helper was tried first and produced empty pages: it
 * scales from CSS pixels to PDF units with assumptions our millimetre layout
 * does not satisfy. Driving the capture and the pagination directly is more
 * code and entirely predictable.
 *
 * The rasteriser is html2canvas-pro, not html2canvas. The original stopped being
 * maintained in 2022 and places a text baseline near the bottom of its box rather
 * than at the text's own baseline — invisible in a paragraph, and in a register
 * it drew every player's name through the rule above it. The fork fixes that, and
 * reads the oklch colours Tailwind v4 emits, which the original cannot parse at
 * all.
 *
 * Both libraries are imported on demand. Together they are most of a megabyte,
 * and a coach reading the week's plan on a phone should not pay for them.
 */

import { planPages } from './paginate';

const A4_SHORT_MM = 210;
const A4_LONG_MM = 297;

/** How far above a page boundary we will look for a gap to break on. */
const SEARCH_BAND = 0.14;

/** White space kept below the last mark on the page, in millimetres. */
const BOTTOM_MARGIN_MM = 8;

export async function exportReportPdf(node: HTMLElement, filename: string): Promise<void> {
  const { jsPDF } = await import('jspdf');

  // The sheet itself, not the wrapper that carries the screen's drop shadow.
  const sheet = node.querySelector<HTMLElement>('.report-sheet') ?? node;

  // Enough that small type survives rasterising, not so much that a long report
  // exhausts the canvas area limit on a phone.
  const canvas = await sheetCanvas(node, Math.min(2, Math.max(1, 2400 / sheet.scrollWidth)));

  // The sheet tells us which way round it is, so one exporter serves both.
  const landscape = sheet.dataset.orientation === 'landscape';
  const pageWidthMm = landscape ? A4_LONG_MM : A4_SHORT_MM;
  const pageHeightMm = landscape ? A4_SHORT_MM : A4_LONG_MM;

  const pdf = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: landscape ? 'landscape' : 'portrait',
  });
  const pxPerMm = canvas.width / pageWidthMm;
  const pageHeightPx = Math.floor(pageHeightMm * pxPerMm);

  // The sheet is at least a full page tall on screen, so a report that ends
  // two thirds of the way down carries a tail of white space with it.
  const content = trimTrailingBlank(canvas, Math.round(BOTTOM_MARGIN_MM * pxPerMm));

  const plan = planPages(content, pageHeightPx, (ideal) =>
    breakPoint(canvas, ideal, pageHeightPx),
  );
  const width = pageWidthMm * plan.shrink;
  const left = (pageWidthMm - width) / 2;

  const slice = document.createElement('canvas');
  slice.width = canvas.width;
  const ctx = slice.getContext('2d');
  if (!ctx) throw new Error('canvas unavailable');

  plan.pages.forEach((page, index) => {
    slice.height = page.height;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, slice.width, page.height);
    ctx.drawImage(
      canvas,
      0,
      page.top,
      canvas.width,
      page.height,
      0,
      0,
      canvas.width,
      page.height,
    );

    if (index > 0) pdf.addPage();
    pdf.addImage(
      slice.toDataURL('image/jpeg', 0.92),
      'JPEG',
      left,
      0,
      width,
      (page.height / pxPerMm) * plan.shrink,
    );
  });

  pdf.save(filename);
}

/**
 * The height of the canvas with its trailing white space removed.
 *
 * The sheet is `min-height: 297mm` so that a short report still looks like a
 * page on screen. Carrying that padding into the file turns a one-page report
 * into two, the second holding nothing.
 */
function trimTrailingBlank(canvas: HTMLCanvasElement, margin: number): number {
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas.height;

  const look = Math.min(canvas.height, Math.round(canvas.height * 0.5));
  const from = canvas.height - look;
  let data: Uint8ClampedArray;
  try {
    data = ctx.getImageData(0, from, canvas.width, look).data;
  } catch {
    return canvas.height;
  }

  for (let y = look - 1; y >= 0; y--) {
    for (let x = 0; x < canvas.width; x += 4) {
      const i = (y * canvas.width + x) * 4;
      if (data[i]! < 246 || data[i + 1]! < 246 || data[i + 2]! < 246) {
        return Math.min(canvas.height, from + y + margin);
      }
    }
  }
  return canvas.height;
}

/**
 * Finds a horizontal line of blank pixels to cut on, at or above `ideal`.
 *
 * Cutting a page at exactly 297mm slices through whichever table row happens to
 * be there, and half a player's name at the foot of a page is the difference
 * between a document somebody files and one they redo by hand. Searching a band
 * above the boundary costs one pass over a few hundred rows of pixels.
 */
function breakPoint(canvas: HTMLCanvasElement, idealFloat: number, pageHeight: number): number {
  const ideal = Math.floor(idealFloat);
  const ctx = canvas.getContext('2d');
  if (!ctx) return ideal;

  const band = Math.floor(pageHeight * SEARCH_BAND);
  const from = Math.max(1, ideal - band);
  const height = ideal - from;
  if (height <= 0) return ideal;

  let data: Uint8ClampedArray;
  try {
    data = ctx.getImageData(0, from, canvas.width, height).data;
  } catch {
    // A tainted canvas cannot be read back. Cut on the boundary rather than fail.
    return ideal;
  }

  for (let y = height - 1; y >= 0; y--) {
    let blank = true;
    // Every fourth pixel is enough to spot a rule or a line of text, and four
    // times cheaper across a full-width row.
    for (let x = 0; x < canvas.width; x += 4) {
      const i = (y * canvas.width + x) * 4;
      if (data[i]! < 246 || data[i + 1]! < 246 || data[i + 2]! < 246) {
        blank = false;
        break;
      }
    }
    if (blank) return from + y;
  }
  return ideal;
}

/**
 * Capturing a sheet as a picture, for the places a PDF is the wrong object.
 *
 * A call-up goes into a WhatsApp group, where an image is read in the thread and
 * a PDF is an attachment somebody has to decide to open. Same sheet, same
 * capture, different container.
 */
async function sheetCanvas(node: HTMLElement, scale: number): Promise<HTMLCanvasElement> {
  const { default: rasterise } = await import('html2canvas-pro');
  const sheet = node.querySelector<HTMLElement>('.report-sheet') ?? node;
  return rasterise(sheet, {
    scale,
    useCORS: true,
    backgroundColor: '#ffffff',
    logging: false,
    windowWidth: sheet.scrollWidth,
    // A sheet with a fixed height — a register, a call-up — crops anything that
    // does not fit, and a cropped capture loses rows silently, which is the worst
    // way for this to fail. Capturing the scroll height instead turns that into
    // an honest second page. For every other report the two are the same number.
    height: Math.max(sheet.scrollHeight, sheet.clientHeight),
    scrollX: 0,
    scrollY: 0,
  });
}

/** Big enough to stay sharp when a phone zooms in, small enough to send. */
const IMAGE_TARGET_PX = 1400;

async function sheetBlob(node: HTMLElement): Promise<Blob> {
  const sheet = node.querySelector<HTMLElement>('.report-sheet') ?? node;
  const scale = Math.min(4, Math.max(2, IMAGE_TARGET_PX / sheet.scrollWidth));
  const canvas = await sheetCanvas(node, scale);
  const blob = await new Promise<Blob | null>((resolve) =>
    // PNG, not JPEG: this is flat colour and type, where JPEG puts a halo around
    // every letter and saves nothing worth having.
    canvas.toBlob((b) => resolve(b), 'image/png'),
  );
  if (!blob) throw new Error('capture failed');
  return blob;
}

export async function exportReportImage(node: HTMLElement, filename: string): Promise<void> {
  const blob = await sheetBlob(node);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  // Revoking immediately cancels the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** True when this device can hand a file straight to another app. */
export function canShareImage(): boolean {
  if (typeof navigator === 'undefined' || !navigator.canShare || !navigator.share) return false;
  try {
    return navigator.canShare({ files: [new File([], 'x.png', { type: 'image/png' })] });
  } catch {
    return false;
  }
}

/**
 * Hands the picture to the phone's own share sheet, which is where WhatsApp is.
 * Returns false when the coach dismisses it, so the caller can stay quiet rather
 * than reporting a failure that was a decision.
 */
export async function shareReportImage(
  node: HTMLElement,
  filename: string,
  title: string,
): Promise<boolean> {
  const blob = await sheetBlob(node);
  const file = new File([blob], filename, { type: 'image/png' });
  try {
    await navigator.share({ files: [file], title });
    return true;
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') return false;
    throw e;
  }
}

/**
 * Prints one sheet when the screen is carrying more than one.
 *
 * The match screen holds both a call-up and a minutes plan, and the print
 * stylesheet keeps every `.report-preview` on the page — which is right when
 * there is one and wrong when the coach pressed Print on the second of two. The
 * others are marked for the duration of the print and unmarked afterwards, so
 * nothing about the screen is left changed by having printed.
 */
export function printOnly(node: HTMLElement): void {
  const target = node.closest('.report-preview') ?? node;
  const others = [...document.querySelectorAll<HTMLElement>('.report-preview')].filter(
    (el) => el !== target,
  );
  others.forEach((el) => el.setAttribute('data-print-hidden', ''));

  const restore = () => {
    others.forEach((el) => el.removeAttribute('data-print-hidden'));
    window.removeEventListener('afterprint', restore);
  };
  window.addEventListener('afterprint', restore);
  window.print();
  // Safari fires afterprint unreliably; a timer is the belt to that braces.
  setTimeout(restore, 2000);
}

/** A4 portrait at a size that stays crisp when a canvas page is placed in a PDF. */
export const PAGE_WIDTH_PX = 1240;
export const PAGE_HEIGHT_PX = Math.round((PAGE_WIDTH_PX * 297) / 210);

/**
 * A PDF of several pages, each already drawn on its own canvas.
 *
 * The minutes plan is one pitch per period, and a pitch is drawn rather than laid
 * out — so the pages arrive as canvases and go straight in, one per sheet, at
 * full bleed. No capture, no pagination: the page and the canvas are the same
 * shape by construction.
 */
export async function exportCanvasPagesPdf(
  pages: readonly HTMLCanvasElement[],
  filename: string,
): Promise<void> {
  if (pages.length === 0) return;
  const { jsPDF } = await import('jspdf');
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  pages.forEach((canvas, index) => {
    if (index > 0) pdf.addPage();
    pdf.addImage(canvas.toDataURL('image/jpeg', 0.94), 'JPEG', 0, 0, 210, 297);
  });
  pdf.save(filename);
}

/** A filename a coach can find again: what it is, who it is about, when. */
export function reportFilename(
  parts: (string | null | undefined)[],
  onDate: string,
  extension = 'pdf',
): string {
  const slug = parts
    .filter((p): p is string => Boolean(p && p.trim()))
    .join(' ')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 70);
  return `${slug || 'report'}-${onDate}.${extension}`;
}
